import React from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

import { resolveToken } from './themeTokens';

// CSS class definitions and inline `style` resolve `var(--...)` natively, but
// Recharts' `fill` / `stroke` props are forwarded to native SVG attributes
// which do NOT — so we keep the var() strings for swatches (inline style) and
// resolve to concrete hex strings for the donut Cells below.
const COLOR_BY_KIND = {
  total: 'var(--setlist-blue-500)',
  genre: 'var(--setlist-purple-500)',
  similar: 'var(--setlist-success-500)',
  artist: 'var(--setlist-orange-500)',
  first_timer: 'var(--setlist-error-500)',
};

const SVG_COLOR_BY_KIND_FALLBACKS = {
  total: '#2E90FA',
  genre: '#7A5AF8',
  similar: '#A2DC47',
  artist: '#EF6820',
  first_timer: '#F04438',
};

const SVG_COLOR_TOKEN_BY_KIND = {
  total: '--setlist-blue-500',
  genre: '--setlist-purple-500',
  similar: '--setlist-success-500',
  artist: '--setlist-orange-500',
  first_timer: '--setlist-error-500',
};

const MOCK_DATA = {
  unique_buyers: 156,
  first_timer_pct: 59,
  insight: 'Most buyers are new, but your artist fans converted hard — 14 of 19 came back this run.',
  segments: [
    { kind: 'total',       name: 'Total buyers',        count: 156, pct: 100, subtitle: null },
    { kind: 'genre',       name: 'Genre fans',          count: 38,  pct: 24,  subtitle: '38 of 281 known fans' },
    { kind: 'similar',     name: 'Similar artist fans', count: 12,  pct: 8,   subtitle: '12 of 64 known fans' },
    { kind: 'artist',      name: 'Artist fans',         count: 14,  pct: 9,   subtitle: '14 of 19 known fans' },
    { kind: 'first_timer', name: 'First timers',        count: 92,  pct: 59,  subtitle: '58.97% of all buyers' },
  ],
};

const formatCount = (n) => new Intl.NumberFormat('en-US').format(n);

const isRealPayload = (data) =>
  data && typeof data === 'object' && Array.isArray(data.segments) && data.segments.length > 0;

const LightbulbIcon = () => (
  <svg
    className="mi-buyer-mix-insight-icon"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.6 1 1.4 1 2.3v1h6v-1c0-.9.4-1.7 1-2.3A7 7 0 0 0 12 2Z" />
  </svg>
);

const Subtitle = ({ uniqueBuyers, firstTimerPct, venueName }) => {
  if (uniqueBuyers === 0) return null;
  const venueSuffix = venueName ? ` to ${venueName}` : '';
  return (
    <p className="mi-audience-card-subtitle">
      <strong>{formatCount(uniqueBuyers)}</strong> unique buyer{uniqueBuyers === 1 ? '' : 's'} ·{' '}
      <strong>{firstTimerPct}%</strong> new{venueSuffix}
    </p>
  );
};

const DonutCenter = ({ uniqueBuyers, firstTimerPct }) => {
  const allFirstTimers = firstTimerPct >= 100;
  return (
    <div className="mi-buyer-mix-donut-center">
      <span className="mi-buyer-mix-donut-big">
        {allFirstTimers ? '100%' : formatCount(uniqueBuyers)}
      </span>
      <span className="mi-buyer-mix-donut-small">
        {allFirstTimers ? 'first-timers' : `buyer${uniqueBuyers === 1 ? '' : 's'}`}
      </span>
    </div>
  );
};

const BuyerMixCard = ({ data, venueName }) => {
  const isReal = isRealPayload(data);
  const payload = isReal ? data : MOCK_DATA;
  const { unique_buyers: uniqueBuyers, first_timer_pct: firstTimerPct, insight, segments } = payload;
  const [totalSegment, ...subSegments] = segments;

  const donutData = subSegments
    .filter((s) => s.count > 0)
    .map((s) => ({ name: s.name, value: s.count, kind: s.kind }));
  const hasDonutData = donutData.length > 0;

  const svgColorByKind = React.useMemo(() => {
    const out = {};
    Object.keys(SVG_COLOR_TOKEN_BY_KIND).forEach((kind) => {
      out[kind] = resolveToken(
        SVG_COLOR_TOKEN_BY_KIND[kind],
        SVG_COLOR_BY_KIND_FALLBACKS[kind],
      );
    });
    return out;
  }, []);

  return (
    <section className="mi-audience-card mi-buyer-mix-card" aria-label="Buyer mix">
      <div className="mi-audience-card-head">
        <div>
          <h3 className="mi-audience-card-title">Buyer mix</h3>
          <Subtitle
            uniqueBuyers={uniqueBuyers}
            firstTimerPct={firstTimerPct}
            venueName={venueName}
          />
        </div>
        {!isReal && <span className="mi-sample-pill">Sample data</span>}
      </div>

      {isReal && uniqueBuyers === 0 ? (
        <p className="mi-audience-empty">No paid buyers yet.</p>
      ) : (
        <>
          <hr className="mi-buyer-mix-divider" />

          <div className="mi-buyer-mix-body">
            <div className="mi-buyer-mix-donut">
              {hasDonutData && (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey="value"
                      innerRadius="68%"
                      outerRadius="100%"
                      startAngle={90}
                      endAngle={-270}
                      stroke="none"
                    >
                      {donutData.map((s) => (
                        <Cell key={s.kind} fill={svgColorByKind[s.kind]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              )}
              <DonutCenter uniqueBuyers={uniqueBuyers} firstTimerPct={firstTimerPct} />
            </div>

            <div className="mi-buyer-mix-kpi">
              <div className="mi-buyer-mix-kpi-row is-total">
                <span className="mi-buyer-mix-kpi-name">{totalSegment.name}</span>
                <span className="mi-buyer-mix-kpi-value">
                  {formatCount(totalSegment.count)}
                </span>
              </div>
              {subSegments.map((s) => (
                <div key={s.kind} className="mi-buyer-mix-kpi-row">
                  <div className="mi-buyer-mix-kpi-left">
                    <span
                      className="mi-buyer-mix-kpi-swatch"
                      style={{
                        background: COLOR_BY_KIND[s.kind],
                        opacity: s.count === 0 ? 0.4 : 1,
                      }}
                    />
                    <div className="mi-buyer-mix-kpi-text">
                      <span className="mi-buyer-mix-kpi-name">{s.name}</span>
                      {s.subtitle && (
                        <span className="mi-buyer-mix-kpi-subtitle">{s.subtitle}</span>
                      )}
                    </div>
                  </div>
                  <span className="mi-buyer-mix-kpi-value">{formatCount(s.count)}</span>
                </div>
              ))}
            </div>
          </div>

          {insight && (
            <div className="mi-buyer-mix-insight" role="note">
              <LightbulbIcon />
              <p>{insight}</p>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default BuyerMixCard;
